import React, { useEffect, useRef } from 'react';

// Shared layout for auth pages (login, signup, forgot/reset password) — the dark
// petrol dot-matrix background, glow, and glass card. Reskinned to the Offer Desk
// brand (tokens in styles/tokens.css); fonts load globally via index.css.

function DotMatrix() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const SPACING = 22;
    const BASE_RADIUS = 1.4;
    const WAVE_SPEED = 0.0018;
    const FLICKER_CHANCE = 0.0004;

    let W, H, cols, rows;
    let t = 0;
    let flickerMap = {};
    let animId;

    function resize() {
      W = canvas.width = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
      cols = Math.ceil(W / SPACING) + 1;
      rows = Math.ceil(H / SPACING) + 1;
    }

    function draw() {
      ctx.clearRect(0, 0, W, H);
      t += WAVE_SPEED;

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const x = c * SPACING;
          const y = r * SPACING;
          const key = `${r}_${c}`;

          const wave = Math.sin(t + c * 0.18 + r * 0.14) * 0.5 + 0.5;
          const wave2 = Math.cos(t * 0.7 + c * 0.12 - r * 0.09) * 0.5 + 0.5;
          let brightness = (wave * 0.6 + wave2 * 0.4);

          if (!flickerMap[key] && Math.random() < FLICKER_CHANCE) {
            flickerMap[key] = { v: 1, decay: 0.04 + Math.random() * 0.06 };
          }
          if (flickerMap[key]) {
            brightness = Math.max(brightness, flickerMap[key].v);
            flickerMap[key].v -= flickerMap[key].decay;
            if (flickerMap[key].v <= 0) delete flickerMap[key];
          }

          const alpha = 0.08 + brightness * 0.42;
          const radius = BASE_RADIUS * (0.6 + brightness * 0.6);

          // Petrol-teal dots — ramp from deep petrol (#3a5468) to light wing (#7cacb7)
          const r_ch = Math.round(58 + brightness * 66);   // 58 -> 124
          const g_ch = Math.round(84 + brightness * 88);   // 84 -> 172
          const b_ch = Math.round(104 + brightness * 79);  // 104 -> 183

          ctx.beginPath();
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${r_ch},${g_ch},${b_ch},${alpha})`;
          ctx.fill();
        }
      }

      animId = requestAnimationFrame(draw);
    }

    resize();
    draw();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
}

export default function AuthShell({ children }) {
  return (
    <div style={{
      minHeight: '100vh', background: 'var(--tandm-gradient-hero-dark)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--tandm-font-sans)', position: 'relative', overflow: 'hidden',
    }}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100%{opacity:1;} 50%{opacity:0.3;} }
        @keyframes glowPulse {
          0%,100% { box-shadow: 0 0 0 0 rgba(108,150,156,0); }
          50%     { box-shadow: 0 0 0 6px rgba(108,150,156,0.14); }
        }

        .lf-card {
          animation: fadeUp 0.55s cubic-bezier(0.16,1,0.3,1) both;
        }

        .lf-input {
          width: 100%; padding: 13px 16px;
          background: rgba(255,255,255,0.05);
          border: 1.5px solid rgba(255,255,255,0.12);
          border-radius: 12px;
          font-size: 14px; font-family: var(--tandm-font-sans);
          color: var(--tandm-on-dark); outline: none;
          transition: all 0.2s;
        }
        .lf-input:focus {
          border-color: var(--tandm-accent-hot);
          background: rgba(108,150,156,0.08);
          box-shadow: 0 0 0 3px rgba(108,150,156,0.16);
        }
        .lf-input::placeholder { color: rgba(255,255,255,0.28); }

        .lf-label {
          display: block; font-size: 11px; font-weight: 600;
          color: rgba(255,255,255,0.5); margin-bottom: 8px;
          text-transform: uppercase; letter-spacing: 0.06em;
          font-family: var(--tandm-font-sans);
        }

        .lf-btn {
          width: 100%; padding: 14px;
          background: var(--tandm-gradient-cta);
          color: var(--tandm-on-accent); border: none; border-radius: 12px;
          font-size: 14px; font-weight: 600; font-family: var(--tandm-font-sans);
          cursor: pointer; transition: opacity 0.2s, transform 0.15s;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          animation: glowPulse 3s ease-in-out infinite;
        }
        .lf-btn:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); }
        .lf-btn:disabled { opacity: 0.5; cursor: not-allowed; animation: none; }

        .lf-spinner {
          width: 15px; height: 15px;
          border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff;
          border-radius: 50%; animation: spin 0.65s linear infinite;
        }

        .pw-wrap { position: relative; }
        .pw-btn {
          position: absolute; right: 13px; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: rgba(255,255,255,0.35); display: flex; align-items: center;
          padding: 0; transition: color 0.15s;
        }
        .pw-btn:hover { color: var(--tandm-accent-light); }

        .lf-link {
          color: var(--tandm-accent-light); font-weight: 600; text-decoration: none;
          transition: opacity 0.15s;
        }
        .lf-link:hover { opacity: 0.8; }

        .lf-divider {
          display: flex; align-items: center; gap: 12px; margin: 6px 0;
        }
        .lf-divider-line {
          flex: 1; height: 1px; background: rgba(255,255,255,0.1);
        }
        .lf-divider-text {
          font-size: 11px; color: rgba(255,255,255,0.3); white-space: nowrap;
        }
      `}</style>

      <DotMatrix />

      <div style={{
        position: 'absolute', width: 600, height: 600, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(108,150,156,0.12) 0%, transparent 65%)',
        pointerEvents: 'none',
      }} />

      <div className="lf-card" style={{
        position: 'relative', zIndex: 10,
        width: '100%', maxWidth: 420,
        background: 'rgba(11,18,32,0.78)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid rgba(124,172,183,0.2)',
        borderRadius: 24, padding: '40px 36px',
        boxShadow: '0 32px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
        margin: '16px',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 36 }}>
          <img src="/tandm-logo-on-dark.png" style={{ height: 26, width: 'auto', objectFit: 'contain', display: 'block' }} alt="Tandm" />
          <div style={{
            width: 5, height: 5, borderRadius: '50%', background: 'var(--tandm-accent-hot)',
            animation: 'pulse 2s ease-in-out infinite',
          }} />
        </div>

        {children}
      </div>
    </div>
  );
}
